import { useState, useEffect, useCallback, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { format, parse, subDays, addDays, startOfDay } from 'date-fns';
import ProductSelector from './ProductSelector';
import TimeRangeSelector from './TimeRangeSelector';
import Toggle from './Toggle';
import NPSChart from './NPSChart';
import LLMQueriesDisplay from './LLMQueriesDisplay';
import LLMPersonaSelector, { ALL_PERSONAS, apiPersona } from './LLMPersonaSelector';
import LLMPromptMovers from './LLMPromptMovers';
import { apiPost } from '../services/apiClient';
import '../styles/NPSPage.css';
import '../styles/LLMRecommendationsPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Main product + competitor colors (same palette as NPSPage).
const MAIN_COLOR = { line: '#0057FF', fill: 'rgba(0, 87, 255, 0.1)' };
const COMPETITOR_COLORS = [
    { line: '#FF8A00', fill: 'rgba(255, 138, 0, 0.1)' },
    { line: '#00A86B', fill: 'rgba(0, 168, 107, 0.1)' },
    { line: '#7A1FFF', fill: 'rgba(122, 31, 255, 0.1)' },
    { line: '#E63946', fill: 'rgba(230, 57, 70, 0.1)' },
    { line: '#0891B2', fill: 'rgba(8, 145, 178, 0.1)' },
    { line: '#CA8A04', fill: 'rgba(202, 138, 4, 0.1)' },
];

// The LLM tables are recent, so day-granularity ranges make the most sense.
const PERIODS = ['10d', '30d', '90d', '6mo', '1y', 'all'];
const PERIOD_LABELS = { '10d': '10D', '30d': '30D', '90d': '90D', '6mo': '6M', '1y': '1Y', 'all': 'All' };
// Days of look-back per period. 'all' is capped at 2y (covers all current data
// while keeping the daily series a sane size).
const PERIOD_DAYS = { '10d': 10, '30d': 30, '90d': 90, '6mo': 180, '1y': 365, 'all': 730 };

const STAT_NAME = 'LLM Rec';

// Prompt-type filter. 'both' sends no filter (all prompts); the API takes
// 'static' | 'dynamic' | null and matches chatgpt_queries.prompt_type.
const PROMPT_TYPES = ['both', 'static', 'dynamic'];
const PROMPT_TYPE_LABELS = { both: 'Static + Dynamic', static: 'Static', dynamic: 'Dynamic' };
const apiPromptType = (pt) => (pt === 'static' || pt === 'dynamic' ? pt : null);

function rangeDates(timeRange) {
    const days = PERIOD_DAYS[timeRange] || 90;
    return {
        startDate: format(subDays(new Date(), days), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    };
}

// Fetch one topic's daily smoothed series. Every series — the main product and
// each competitor — is scored the same way: over that topic's OWN assessment
// rows. That is what makes a brand's score identical no matter whose page it is
// plotted on. (Competitors used to be scored over any prompt that merely
// MENTIONED them, which averaged in every other brand's rating.)
//  promptType='static'|'dynamic' scopes to that prompt class; null = both.
//  persona keeps only prompts asked as that persona; null = all prompts.
function fetchSeries(topicId, startDate, endDate, promptType = null, persona = null) {
    return apiPost('topic/llm-recommendations/moving-average', {
        topic_id: topicId,
        start_date: startDate,
        end_date: endDate,
        prompt_type: promptType,
        persona,
    })
        .then(r => r.json())
        .then(result => (Array.isArray(result?.data) ? result.data : []))
        .catch(err => {
            console.error('[LLMRecommendationsPage] series error:', err);
            return [];
        });
}

const hasSignal = (rows) => Array.isArray(rows) && rows.some(r => (r.count || 0) > 0);

// Card mirroring NPSPage's MetricCard, for the LLM recommendation score.
const MetricCard = ({ period, metrics, onClick }) => {
    const cls = (v) => (v == null ? 'neutral' : v >= 70 ? 'positive' : v < 50 ? 'negative' : 'neutral');
    return (
        <div className="metric-card" onClick={onClick}>
            <h3>{period}</h3>
            <div className="metric-row">
                <span className="metric-label">{STAT_NAME} Score</span>
                <span className={`metric-value ${cls(metrics?.score)}`}>
                    {metrics?.score ?? '-'}
                </span>
            </div>
            <div className="sample-size">
                Responses: {metrics?.count || 0}
            </div>
        </div>
    );
};

// Weighted average of the smoothed daily score over the last `days`, weighting
// each real (non-empty) day by its response count.
function periodAverage(rows, days) {
    if (!rows || rows.length === 0) return { score: null, count: 0 };
    const cutoff = format(subDays(new Date(), days), 'yyyy-MM-dd');
    const inRange = rows.filter(r => r.day >= cutoff && r.count > 0);
    if (inRange.length === 0) return { score: null, count: 0 };

    let weighted = 0, totalWeight = 0, totalCount = 0;
    inRange.forEach(r => {
        const w = r.count || 1;
        weighted += (r.mean_nps || 0) * w;
        totalWeight += w;
        totalCount += r.count || 0;
    });
    const score = totalWeight > 0 ? Math.round(weighted / totalWeight) : null;
    return { score, count: totalCount };
}

// initialTimeRange / initialShowCompetitors let the report generator's
// off-screen capture open the page on a specific window with competitor
// lines on; both default to the interactive page's normal behaviour.
const LLMRecommendationsPage = ({ topicMap, initialTimeRange = '90d', initialShowCompetitors = false }) => {
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductName, setSelectedProductName] = useState('');
    const [timeRange, setTimeRange] = useState(initialTimeRange);
    const [promptType, setPromptType] = useState('both');
    const [persona, setPersona] = useState(ALL_PERSONAS);
    const [showConfidenceIntervals, setShowConfidenceIntervals] = useState(false);
    const [showCompetitors, setShowCompetitors] = useState(initialShowCompetitors);
    const [wholeCompany, setWholeCompany] = useState(false);

    const [rows, setRows] = useState([]);                    // main product daily series
    const [competitorSeries, setCompetitorSeries] = useState([]); // [{ id, name, rows }]
    const [isLoading, setIsLoading] = useState(false);
    const [compLoading, setCompLoading] = useState(false);

    const [queries, setQueries] = useState([]);
    const [showQueries, setShowQueries] = useState(false);
    const [queriesLoading, setQueriesLoading] = useState(false);
    const [drilldownTitle, setDrilldownTitle] = useState('');

    // Competitors come from the SAME place the NPS page uses.
    const competitors = useMemo(
        () => (selectedProduct && topicMap?.[selectedProduct]?.competitors) || [],
        [selectedProduct, topicMap]
    );

    // The company's full brand set — what "Entire Company" averages over.
    const allTopicIds = useMemo(
        () => Object.values(topicMap || {}).filter(t => t && t.id).map(t => t.id),
        [topicMap]
    );

    // Track the product name for labelling.
    useEffect(() => {
        if (selectedProduct && topicMap?.[selectedProduct]) {
            setSelectedProductName(topicMap[selectedProduct].name || topicMap[selectedProduct].topic || '');
        }
    }, [selectedProduct, topicMap]);

    // Personas are topic-specific, so a selection can't carry across products.
    useEffect(() => {
        setPersona(ALL_PERSONAS);
    }, [selectedProduct]);

    // Fetch the main series: one brand, or the whole company (every brand
    // scored independently, then averaged with equal weight per brand — not
    // volume-weighted, and no answer counted twice in any pool).
    useEffect(() => {
        if (!selectedProduct) return;
        const { startDate, endDate } = rangeDates(timeRange);

        let cancelled = false;
        setIsLoading(true);
        setShowQueries(false);
        setQueries([]);

        const request = wholeCompany
            ? apiPost('topic/llm-recommendations/company-moving-average', {
                topic_id: selectedProduct,     // access-control anchor
                topic_ids: allTopicIds,
                start_date: startDate,
                end_date: endDate,
                prompt_type: apiPromptType(promptType),
                persona: apiPersona(persona),
            })
                .then(r => r.json())
                .then(result => (Array.isArray(result?.data) ? result.data : []))
                .catch(err => {
                    console.error('[LLMRecommendationsPage] company series error:', err);
                    return [];
                })
            : fetchSeries(selectedProduct, startDate, endDate, apiPromptType(promptType), apiPersona(persona));

        request
            .then(data => { if (!cancelled) setRows(data); })
            .finally(() => { if (!cancelled) setIsLoading(false); });

        return () => { cancelled = true; };
    }, [selectedProduct, timeRange, promptType, persona, wholeCompany, allTopicIds]);

    // Fetch competitor series the SAME way as the main one, when toggled on.
    // Not meaningful against the whole-company line, whose peers aren't the
    // selected brand's competitor list.
    useEffect(() => {
        if (!showCompetitors || !selectedProduct || wholeCompany) {
            setCompetitorSeries([]);
            return;
        }
        const comps = competitors.filter(c => c.id);
        if (comps.length === 0) {
            setCompetitorSeries([]);
            return;
        }
        const { startDate, endDate } = rangeDates(timeRange);

        let cancelled = false;
        setCompLoading(true);
        Promise.all(
            comps.map(c =>
                fetchSeries(c.id, startDate, endDate, apiPromptType(promptType), apiPersona(persona)).then(seriesRows => ({
                    id: c.id,
                    name: c.topic || c.alt_name || c.id,
                    rows: seriesRows,
                }))
            )
        )
            .then(results => {
                // Only plot competitors that actually have LLM data in range.
                if (!cancelled) setCompetitorSeries(results.filter(r => hasSignal(r.rows)));
            })
            .finally(() => { if (!cancelled) setCompLoading(false); });

        return () => { cancelled = true; };
    }, [showCompetitors, selectedProduct, timeRange, competitors, promptType, persona, wholeCompany]);

    const hasData = useMemo(() => hasSignal(rows), [rows]);

    // Build Chart.js data: main + competitor series (each with optional CI bands).
    const chartData = useMemo(() => {
        if (!rows || rows.length === 0) return null;

        const series = [
            {
                id: selectedProduct,
                name: wholeCompany ? 'Entire Company' : (selectedProductName || 'LLM Recommendation Score'),
                rows,
                colorSet: MAIN_COLOR,
                isMain: true,
            },
            ...competitorSeries.map((c, i) => ({
                id: c.id,
                name: c.name,
                rows: c.rows,
                colorSet: COMPETITOR_COLORS[i % COMPETITOR_COLORS.length],
                isMain: false,
            })),
        ];

        // Unified, sorted day labels across every series.
        const daySet = new Set();
        series.forEach(s => s.rows.forEach(r => daySet.add(r.day)));
        const labels = [...daySet].sort();

        const datasets = [];
        series.forEach(s => {
            const byDay = {};
            s.rows.forEach(r => { byDay[r.day] = r; });

            datasets.push({
                label: s.name,
                data: labels.map(d => {
                    const r = byDay[d];
                    return r
                        ? { x: d, y: r.mean_nps, confidenceBounds: { lower: r.lower_ci, upper: r.upper_ci }, count: r.count, windowCount: r.window_count, isForwardFilled: r.is_forward_filled }
                        : { x: d, y: null };
                }),
                borderColor: s.colorSet.line,
                backgroundColor: s.colorSet.fill,
                borderWidth: s.isMain ? 3 : 2,
                tension: 0.1,
                pointRadius: 2,
                pointHoverRadius: 5,
                spanGaps: true,
                topicId: s.id,          // used by the click handler for drill-down
            });

            if (showConfidenceIntervals) {
                datasets.push({
                    label: `${s.name} Upper Confidence Band`,
                    data: labels.map(d => { const r = byDay[d]; return r ? { x: d, y: r.upper_ci } : { x: d, y: null }; }),
                    borderColor: s.colorSet.line, borderWidth: 1, borderDash: [5, 5], pointRadius: 0, fill: false, tension: 0.1, spanGaps: true,
                });
                datasets.push({
                    label: `${s.name} Lower Confidence Band`,
                    data: labels.map(d => { const r = byDay[d]; return r ? { x: d, y: r.lower_ci } : { x: d, y: null }; }),
                    borderColor: s.colorSet.line, borderWidth: 1, borderDash: [5, 5], pointRadius: 0, fill: '-1', backgroundColor: s.colorSet.fill, tension: 0.1, spanGaps: true,
                });
            }
        });

        return { labels, datasets };
    }, [rows, competitorSeries, showConfidenceIntervals, selectedProductName, selectedProduct, wholeCompany]);

    const averages = useMemo(() => ({
        month: periodAverage(rows, 30),
        quarter: periodAverage(rows, 90),
        year: periodAverage(rows, 365),
    }), [rows]);

    // Shared fetch for the drill-down (a clicked day on any series, a metric-card
    // period, or a prompt mover's before/after answers). Always scoped to the
    // clicked series' own topic, so every row listed is one that actually
    // contributed to the point you clicked.
    //  opts.promptId scopes to one prompt; opts.minConfidence overrides the
    //  default confidence gate (null disables it — used for movers, where the
    //  answers must show regardless of assessment confidence).
    const fetchQueries = useCallback(async (topicId, startDate, endDate, title, opts = {}) => {
        if (!topicId) return;
        setQueriesLoading(true);
        setDrilldownTitle(title);
        try {
            const body = {
                topic_id: topicId,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                limit: 100,
                prompt_type: apiPromptType(promptType),
                persona: apiPersona(persona),
            };
            if (opts.promptId) body.prompt_id = opts.promptId;
            if ('minConfidence' in opts) body.min_confidence = opts.minConfidence;
            const res = await apiPost('topic/llm-recommendations/queries', body);
            const result = await res.json();
            setQueries(result?.data || []);
            setShowQueries(true);
        } catch (err) {
            console.error('[LLMRecommendationsPage] queries error:', err);
            setQueries([]);
            setShowQueries(true);
        } finally {
            setQueriesLoading(false);
        }
    }, [promptType, persona]);

    // Chart point click → that day's prompts + answers for the CLICKED series.
    // The drill-down is per-topic, so the whole-company line has no click-through.
    const handleChartPointClick = useCallback(({ label, datasetIndex }) => {
        if (!label || wholeCompany) return;
        const ds = chartData?.datasets?.[datasetIndex];
        const topicId = ds?.topicId || selectedProduct;
        const dsLabel = ds?.label || '';
        if (!topicId) return;
        const dayStart = startOfDay(parse(label, 'yyyy-MM-dd', new Date()));
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
        const prefix = dsLabel ? `${dsLabel} — ` : '';
        fetchQueries(topicId, dayStart, dayEnd, `${prefix}${format(dayStart, 'MMMM d, yyyy')}`);
    }, [chartData, selectedProduct, fetchQueries, wholeCompany]);

    const handleMetricCardClick = useCallback((days, label) => {
        if (wholeCompany) return; // drill-down is per-topic
        const start = startOfDay(subDays(new Date(), days));
        const end = new Date();
        fetchQueries(selectedProduct, start, end, `${selectedProductName || 'Product'} — ${label}`);
    }, [fetchQueries, selectedProduct, selectedProductName, wholeCompany]);

    // A mover row's "Answers": the prompt's answer on the previous ask vs the
    // day it moved, side by side in the standard drill-down. No confidence
    // gate — the whole point is seeing both answers, however unsure the scorer.
    const handleViewMoverAnswers = useCallback((m) => {
        const prevStart = startOfDay(parse(m.prev_day, 'yyyy-MM-dd', new Date()));
        const dayEnd = addDays(startOfDay(parse(m.day, 'yyyy-MM-dd', new Date())), 1);
        const snippet = m.prompt.length > 70 ? `${m.prompt.slice(0, 70)}…` : m.prompt;
        fetchQueries(
            selectedProduct,
            prevStart,
            dayEnd,
            `“${snippet}” — ${format(prevStart, 'MMM d')} vs ${format(subDays(dayEnd, 1), 'MMM d')}`,
            { promptId: m.prompt_id, minConfidence: null }
        );
    }, [fetchQueries, selectedProduct]);

    return (
        <div className="nps-page-container">
            <div className="controls-section">
                <div className="product-control">
                    <ProductSelector
                        topicMap={topicMap}
                        selectedId={selectedProduct || ''}
                        onSelect={setSelectedProduct}
                    />
                    <LLMPersonaSelector
                        topicId={selectedProduct}
                        value={persona}
                        onChange={setPersona}
                    />
                    {allTopicIds.length > 1 && (
                        <Toggle
                            label="Entire Company"
                            checked={wholeCompany}
                            onChange={setWholeCompany}
                        />
                    )}
                    {competitors.length > 0 && !wholeCompany && (
                        <Toggle
                            label="Show Competitors"
                            checked={showCompetitors}
                            onChange={setShowCompetitors}
                        />
                    )}
                </div>
                <div className="view-controls">
                    <TimeRangeSelector
                        timeRange={promptType}
                        onTimeRangeChange={setPromptType}
                        periods={PROMPT_TYPES}
                        periodLabels={PROMPT_TYPE_LABELS}
                        defaultPeriod="both"
                    />
                    <Toggle
                        label="Show Credible Intervals"
                        checked={showConfidenceIntervals}
                        onChange={setShowConfidenceIntervals}
                    />
                    <TimeRangeSelector
                        timeRange={timeRange}
                        onTimeRangeChange={setTimeRange}
                        periods={PERIODS}
                        periodLabels={PERIOD_LABELS}
                        defaultPeriod="90d"
                    />
                </div>
            </div>

            {isLoading ? (
                <div className="nps-page-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress size={40} />
                </div>
            ) : (
                <>
                    <div className="chart-section">
                        {hasData ? (
                            <>
                                <NPSChart
                                    chartData={chartData}
                                    showConfidenceIntervals={showConfidenceIntervals}
                                    onPointClick={handleChartPointClick}
                                    yAxisTitle="LLM Recommendation Score (0–100)"
                                />
                                <p className="llm-metric-note">
                                    {wholeCompany
                                        ? 'Every brand is scored independently over the answers that name it, then the ' +
                                          'company line averages the brand scores with equal weight — a high-volume brand ' +
                                          'does not dominate, and an answer naming several of the company’s brands is ' +
                                          'never counted twice in any pool. Switch off Entire Company to drill into a ' +
                                          'single brand’s answers.'
                                        : 'Each brand is scored over the answers that name it, whether as the top ' +
                                          'recommendation or further down a list. Answers that never mention the brand are ' +
                                          'excluded, as are assessments the model was not confident about (below 7 of 10). ' +
                                          'How often a brand goes unmentioned is measured on the Competitive Comparison tab. ' +
                                          'A brand’s score is the same wherever it is plotted, so a competitor’s line here ' +
                                          'matches its own page.'}
                                </p>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '48px 16px', color: '#64748b' }}>
                                {selectedProduct
                                    ? 'No LLM recommendation data available for this product in the selected period.'
                                    : 'Select a product to view LLM recommendations.'}
                            </div>
                        )}
                        {showCompetitors && compLoading && (
                            <div style={{ textAlign: 'center', padding: 8, color: '#94a3b8', fontSize: 13 }}>
                                Loading competitors…
                            </div>
                        )}
                    </div>

                    {hasData && (
                        <div className="metrics-section">
                            <div className="metric-cards">
                                <MetricCard period="Past 30 Days" metrics={averages.month} onClick={() => handleMetricCardClick(30, 'Past 30 Days')} />
                                <MetricCard period="Past 90 Days" metrics={averages.quarter} onClick={() => handleMetricCardClick(90, 'Past 90 Days')} />
                                <MetricCard period="Past Year" metrics={averages.year} onClick={() => handleMetricCardClick(365, 'Past Year')} />
                            </div>
                        </div>
                    )}

                    {hasData && !wholeCompany && (
                        <LLMPromptMovers
                            topicId={selectedProduct}
                            startDate={rangeDates(timeRange).startDate}
                            endDate={rangeDates(timeRange).endDate}
                            promptType={apiPromptType(promptType)}
                            persona={apiPersona(persona)}
                            onViewAnswers={handleViewMoverAnswers}
                        />
                    )}
                </>
            )}

            {queriesLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <CircularProgress size={28} />
                </div>
            )}

            {showQueries && !queriesLoading && (
                queries.length > 0 ? (
                    <LLMQueriesDisplay
                        queries={queries}
                        onClose={() => { setShowQueries(false); setQueries([]); }}
                        id="llm-queries"
                        title={drilldownTitle || 'Prompts & ChatGPT Responses'}
                        autoScroll
                        statName={STAT_NAME}
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                        No prompts found for this period.
                    </div>
                )
            )}
        </div>
    );
};

export default LLMRecommendationsPage;
